import {
  IonButton,
  IonInput,
  IonLabel,
  IonProgressBar,
  IonText,
} from '@ionic/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ResponseError } from '@cratehackers/api-client';
import SpotifyService from '../../../../services/Spotify.service';
import LoginButton from '../../../spotify/login-button';
import BuildGenerateCrate from '../../build-generate-crate/build-generate-crate';
import buildStyle from '../build-path.module.scss';
import style from './spotify.module.scss';

const MAX_CRATE_TRACKS = 200;
const REQUEST_TIMEOUT_MS = 30000;

type LoadingState =
  | 'idle'
  | 'fetching-info'
  | 'fetching-tracks'
  | 'hydrating'
  | 'ready'
  | 'error'
  | 'private-error'
  | 'too-many-tracks'
  | 'rate-limited';

function isRateLimitError(error: unknown): boolean {
  if (error instanceof ResponseError) {
    return error.response.status === 429;
  }
  const msg = (error as any)?.message ?? '';
  return msg.includes('429') || msg.includes('RATE_LIMITED');
}

function useLoadingMessage(isLoading: boolean, baseMessage: string): string {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(0);

  useEffect(() => {
    if (!isLoading) {
      setElapsed(0);
      return;
    }
    startRef.current = Date.now();
    const interval = setInterval(() => {
      setElapsed(Date.now() - startRef.current);
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoading]);

  if (!isLoading) return baseMessage;
  if (elapsed > 20000) return 'Taking longer than expected...';
  if (elapsed > 10000) return 'Still working on it...';
  return baseMessage;
}

export default function SpotifyPath() {
  const [playlistLink, setPlaylistLink] = useState<string>('');
  const [playlist, setPlaylist] = useState<any>(null);
  const [isBtnDisabled, setIsBtnDisabled] = useState<boolean>(true);

  const [playlistExtra, setPlaylistExtra] = useState<any[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [loadingProgress, setLoadingProgress] = useState<{
    current: number;
    total: number;
  }>({ current: 0, total: 0 });
  const [errorMessage, setErrorMessage] = useState<string>('');

  const spotifyToken = useSelector(() => SpotifyService.getAccessToken());
  const isSpotifyConnected = !!spotifyToken;
  const prevTokenRef = useRef(spotifyToken);
  const abortRef = useRef<AbortController | null>(null);

  const isFetchingInfo = loadingState === 'fetching-info';
  const isFetchingTracks =
    loadingState === 'fetching-tracks' || loadingState === 'hydrating';
  const fetchInfoMessage = useLoadingMessage(
    isFetchingInfo,
    'Fetching playlist info...',
  );
  const fetchTracksMessage = useLoadingMessage(
    isFetchingTracks,
    loadingState === 'fetching-tracks'
      ? 'Fetching tracks...'
      : 'Getting track details...',
  );

  const fetchPlaylist = useCallback(async (link: string) => {
    if (!link || link.length <= 8) {
      setPlaylist(null);
      setLoadingState('idle');
      return;
    }

    const playListId = extractSpotifyPlaylistId(link);
    const albumId = extractSpotifyAlbumId(link);

    if (!playListId && !albumId) {
      setLoadingState('idle');
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    setPlaylist(null);
    setErrorMessage('');
    setLoadingState('fetching-info');

    try {
      let fetched: any;
      if (playListId) {
        fetched = await SpotifyService.GetPlaylistInfoById(playListId);
      } else {
        fetched = await SpotifyService.GetAlbumInfoById(albumId);
      }

      if (controller.signal.aborted) return;

      const trackCount = fetched?.info?.tracks?.total ?? 0;
      if (trackCount > MAX_CRATE_TRACKS) {
        setPlaylist(fetched);
        setLoadingState('too-many-tracks');
        setErrorMessage(
          `This playlist has ${trackCount} tracks. The maximum crate size is ${MAX_CRATE_TRACKS} tracks. Please use a smaller playlist or split it into multiple crates.`,
        );
        return;
      }

      setPlaylist(fetched);
    } catch (error) {
      if (controller.signal.aborted) {
        setLoadingState('error');
        setErrorMessage(
          'Request timed out. Spotify may be busy — please try again in a moment.',
        );
        return;
      }

      console.error('Error fetching Spotify data:', error);
      setPlaylist(null);

      if (SpotifyService.isPrivateResourceError(error)) {
        setLoadingState('private-error');
        setErrorMessage('This playlist appears to be private.');
      } else if (isRateLimitError(error)) {
        setLoadingState('rate-limited');
        setErrorMessage(
          'Spotify is experiencing high traffic. Please try again in a few minutes.',
        );
      } else {
        setLoadingState('error');
        setErrorMessage(
          'Failed to fetch playlist. Please check the link and try again.',
        );
      }
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    fetchPlaylist(playlistLink);
    return () => abortRef.current?.abort();
  }, [playlistLink, fetchPlaylist]);

  // Auto-retry after Spotify login
  useEffect(() => {
    const hadToken = !!prevTokenRef.current;
    prevTokenRef.current = spotifyToken;

    if (
      !hadToken &&
      spotifyToken &&
      playlistLink &&
      (loadingState === 'private-error' || loadingState === 'error')
    ) {
      fetchPlaylist(playlistLink);
    }
  }, [spotifyToken, playlistLink, loadingState, fetchPlaylist]);

  useEffect(() => {
    async function fetchPlaylistExtra() {
      setPlaylistExtra([]);
      setIsBtnDisabled(true);
      setErrorMessage('');

      const playlistLength = playlist.info.tracks.total;
      const playListId = extractSpotifyPlaylistId(playlistLink);
      const albumId = extractSpotifyAlbumId(playlistLink);

      const initialTrackCount = playlist.tracks?.tracks?.length || 0;
      let totalPages = 1;
      let pageSize = 100;

      if (playListId && playlistLength > 100) {
        totalPages = Math.ceil(playlistLength / 100);
        pageSize = 100;
      } else if (albumId && playlistLength > 50) {
        totalPages = Math.ceil(playlistLength / 50);
        pageSize = 50;
      }

      if (totalPages > 1) {
        setLoadingState('fetching-tracks');
        setLoadingProgress({
          current: initialTrackCount,
          total: playlistLength,
        });

        try {
          if (playListId) {
            for (let i = 1; i < totalPages; i++) {
              const playlistExtraNextSet = await SpotifyService.GetPlaylistById(
                playListId,
                i,
              );
              if (playlistExtraNextSet) {
                const newTracks = playlistExtraNextSet.tracks.tracks;
                setPlaylistExtra((prevState) => [...prevState, ...newTracks]);
                setLoadingProgress({
                  current: initialTrackCount + i * pageSize,
                  total: playlistLength,
                });
              }
            }
          } else if (albumId) {
            for (let i = 1; i < totalPages; i++) {
              const playlistExtraNextSet = await SpotifyService.GetAlbumById(
                albumId,
                i,
                playlist?.info.images[0].url,
              );
              if (playlistExtraNextSet) {
                const newTracks = playlistExtraNextSet.tracks.tracks;
                setPlaylistExtra((prevState) => [...prevState, ...newTracks]);
                setLoadingProgress({
                  current: initialTrackCount + i * pageSize,
                  total: playlistLength,
                });
              }
            }
          }
        } catch (error) {
          console.error('Error fetching extra tracks:', error);
          if (isRateLimitError(error)) {
            setLoadingState('rate-limited');
            setErrorMessage(
              'Spotify is experiencing high traffic. Please try again in a few minutes.',
            );
          } else {
            setLoadingState('error');
            setErrorMessage(
              'Failed to fetch all tracks. Some tracks may be missing.',
            );
          }
          setIsBtnDisabled(false);
          return;
        }
      }

      setLoadingProgress({ current: playlistLength, total: playlistLength });
      setLoadingState('ready');
      setIsBtnDisabled(false);
    }

    if (playlist?.info?.tracks?.total && loadingState !== 'too-many-tracks') {
      fetchPlaylistExtra();
    } else if (loadingState !== 'too-many-tracks') {
      setIsBtnDisabled(true);
    }
  }, [playlist, playlistLink]);

  function extractSpotifyPlaylistId(url: string): string {
    const regex = /playlist\/(\w+)(?:\?|$)/;
    const match = url.match(regex);
    return match ? match[1] : '';
  }

  function extractSpotifyAlbumId(url: string): string {
    const regex = /album\/(\w+)(?:\?|$)/;
    const match = url.match(regex);
    return match ? match[1] : '';
  }

  const buildCrate = () => {
    const combinedPlaylist = [...playlist.tracks.tracks, ...playlistExtra];
    return {
      _id: '',
      Title: playlist.info.name,
      Description: '',
      Tracks: combinedPlaylist || [],
      createdFrom: 'Spotify',
    };
  };

  return (
    <>
      <div className={buildStyle.linkContainer}>
        <IonLabel className="link-label">
          Enter a Spotify playlist or album link
        </IonLabel>
        <div className={buildStyle['build-input-wrapper']}>
          <IonInput
            className={buildStyle['build-input']}
            placeholder="Enter Spotify link  eg: https://open.spotify.com/album/... or  https://open.spotify.com/playlist/..."
            labelPlacement="floating"
            fill="outline"
            value={playlistLink}
            onIonInput={(e: any) => setPlaylistLink(e.detail.value!)}
          >
            <div slot="label">Spotify Link</div>
          </IonInput>
        </div>

        <div className={style.orDivider}>OR</div>
        <div
          onDrop={(e) => {
            e.preventDefault();
            const data = e.dataTransfer.getData('text');
            if (
              data.includes('spotify.com/playlist') ||
              data.includes('spotify.com/album')
            ) {
              setPlaylistLink(data);
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          className={style.dropZone}
        >
          <p>Drag and drop a Spotify URL here</p>
        </div>
      </div>

      {!isSpotifyConnected && loadingState !== 'private-error' && (
        <div className={style.spotifyHint}>
          <span>Have a private playlist? </span>
          <LoginButton small redirectPath="/my-crates/spotify" />
        </div>
      )}

      {/* Loading state: Fetching playlist info */}
      {loadingState === 'fetching-info' && (
        <div className={style.loadingSpinner}>
          <div className={style.spinner}></div>
          <span>{fetchInfoMessage}</span>
        </div>
      )}

      {/* Private playlist error */}
      {loadingState === 'private-error' && (
        <div className={style.privateError}>
          <IonText color="warning">{errorMessage}</IonText>
          {!isSpotifyConnected ? (
            <div className={style.privateErrorAction}>
              <span>Connect your Spotify account to import it:</span>
              <LoginButton small redirectPath="/my-crates/spotify" />
            </div>
          ) : (
            <div className={style.privateErrorAction}>
              <IonText color="medium">
                You're connected to Spotify but don't have access to this
                playlist. Make sure it's shared with your account or ask the
                owner to make it public.
              </IonText>
            </div>
          )}
        </div>
      )}

      {/* Too many tracks warning */}
      {loadingState === 'too-many-tracks' && (
        <div className={style.warningMessage}>
          <IonText color="warning">{errorMessage}</IonText>
        </div>
      )}

      {/* Rate limited error */}
      {loadingState === 'rate-limited' && (
        <div className={style.errorMessage}>
          <IonText color="warning">{errorMessage}</IonText>
          <div style={{ marginTop: '0.5rem' }}>
            <IonButton
              size="small"
              fill="outline"
              onClick={() => fetchPlaylist(playlistLink)}
            >
              Retry
            </IonButton>
          </div>
        </div>
      )}

      {/* Generic error state */}
      {loadingState === 'error' && errorMessage && (
        <div className={style.errorMessage}>
          <IonText color="danger">{errorMessage}</IonText>
          <div style={{ marginTop: '0.5rem' }}>
            <IonButton
              size="small"
              fill="outline"
              onClick={() => fetchPlaylist(playlistLink)}
            >
              Retry
            </IonButton>
          </div>
        </div>
      )}

      {playlist && playlist.info && (
        <>
          <div className={style['result-item']}>
            <img
              src={playlist.info?.images?.[0]?.url ?? ''}
              alt={playlist.info?.name ?? 'Playlist'}
            />
            <div>
              <div>{playlist.info?.name ?? 'Playlist'}</div>
              <small>
                {playlist.info?.owner?.display_name
                  ? `By ${playlist.info.owner.display_name}`
                  : playlist.info?.artists
                    ? `By ${playlist.info.artists.map((a: any) => a.name).join(', ')}`
                    : ''}
              </small>
              <br />
              <small>
                {playlist.info?.tracks?.total ??
                  playlist.tracks?.tracks?.length ??
                  0}{' '}
                tracks
              </small>
            </div>
          </div>

          {/* Loading progress for fetching tracks */}
          {isFetchingTracks && (
            <div className={style.loadingProgress}>
              <div className={style.loadingProgressHeader}>
                <span className={style.loadingProgressText}>
                  {fetchTracksMessage}
                </span>
                <span className={style.loadingProgressCount}>
                  {loadingProgress.current} / {loadingProgress.total}
                </span>
              </div>
              <IonProgressBar
                value={
                  loadingProgress.total > 0
                    ? loadingProgress.current / loadingProgress.total
                    : 0
                }
                color="primary"
              />
            </div>
          )}

          {/* Ready state */}
          {loadingState === 'ready' && (
            <div className={style.readyMessage}>
              <span>Ready to import {loadingProgress.total} tracks</span>
            </div>
          )}
        </>
      )}

      <BuildGenerateCrate
        buildCrateFn={buildCrate}
        isBtnDisabled={isBtnDisabled}
        disabledMessage="Please provide a valid Spotify playlist or album link with tracks"
      />
    </>
  );
}
